import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Calculate as CalculateIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { catalogPresets, suggestCatalogSector } from '../utils/catalogPresets';
import { loadCompanyProfile, saveCompanyProfile } from '../utils/companyProfileStorage';
import { loadActivityMode } from '../utils/activityModeStorage';
import { getDefaultQuoteItemsForActivity } from '../utils/activityDefaults';

const AdvancedQuoteDialog = ({ 
  open, 
  onClose, 
  onSave, 
  editingQuote, 
  companies 
}) => {
  const navigate = useNavigate();

  // Charger la configuration des colonnes depuis le profil entreprise
  const [activeColumns, setActiveColumns] = useState(['reference', 'description', 'quantity', 'unitPrice', 'vatRate', 'totalHT', 'totalTTC']);
  const [globalVatZero, setGlobalVatZero] = useState(false);
  const [selectedSector, setSelectedSector] = useState('services');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [selectedCatalogIds, setSelectedCatalogIds] = useState([]);
  const [assistantInput, setAssistantInput] = useState('');
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogItems, setCatalogItems] = useState([]);
  const [defaultVatRate, setDefaultVatRate] = useState(20);
  const [plumberUrgence, setPlumberUrgence] = useState(false);
  const [newCatalogItem, setNewCatalogItem] = useState({
    label: '',
    description: '',
    unit: 'unité',
    unitPrice: '',
    vatRate: 20
  });

  useEffect(() => {
    const profile = loadCompanyProfile();
    if (profile?.quoteColumns && profile.quoteColumns.length > 0) {
      // Toujours inclure 'description' même si elle n'est pas dans la config
      const columns = profile.quoteColumns.includes('description') 
        ? profile.quoteColumns 
        : ['description', ...profile.quoteColumns];
      setActiveColumns(columns);
    }
    const initialCatalogItems = profile?.catalogItems || [];
    setCatalogItems(initialCatalogItems);
    const liable = profile?.vatLiable !== undefined ? profile.vatLiable : true;
    const defaultRate = profile?.defaultVatRate !== undefined ? profile.defaultVatRate : 20;
    setDefaultVatRate(liable ? defaultRate : 0);
    setGlobalVatZero(!liable);
    const activity = loadActivityMode();
    const activitySector = activity?.presetCatalogId || 'services';
    setSelectedSector(initialCatalogItems.length ? 'custom' : activitySector);
  }, [open]);

  useEffect(() => {
    if (open) {
      const activity = loadActivityMode();
      setSelectedSector(activity?.presetCatalogId || 'services');
      setCatalogSearch('');
      setSelectedCatalogIds([]);
      setAssistantInput('');
      setPlumberUrgence(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (selectedSector !== 'plumber') {
      setPlumberUrgence(false);
      return;
    }
  }, [open, selectedSector]);

  useEffect(() => {
    if (!open) return;

    if (!plumberUrgence) {
      setItems((prev) => prev.filter((item) => !item?.__autoUrgence));
      return;
    }

    setItems((prev) => {
      if (prev.some((item) => item?.__autoUrgence)) return prev;
      const urgence = {
        id: Date.now() + Math.random(),
        description: 'Majoration urgence',
        reference: '',
        quantity: 1,
        unit: 'forfait',
        unitPrice: 60,
        discount: 0,
        vatRate: globalVatZero ? 0 : defaultVatRate,
        totalHT: 0,
        totalTTC: 0,
        deliveryDate: '',
        __autoUrgence: true,
      };
      const totals = calculateItemTotal(urgence);
      return [...prev, { ...urgence, ...totals }];
    });
  }, [open, plumberUrgence, globalVatZero, defaultVatRate]);

  const [formData, setFormData] = useState({
    number: '',
    companyId: '',
    date: new Date().toISOString().split('T')[0],
    validUntil: '',
    status: 'DRAFT',
    notes: '',
    object: '',
    items: []
  });

  const [items, setItems] = useState([
    {
      id: Date.now(),
      description: '',
      reference: '',
      quantity: 1,
      unit: 'unité',
      unitPrice: 0,
      discount: 0,
      vatRate: 20,
      totalHT: 0,
      totalTTC: 0,
      deliveryDate: ''
    }
  ]);

  const [totals, setTotals] = useState({
    subtotalHT: 0,
    vatAmount: 0,
    totalTTC: 0,
    discount: 0,
    finalTotal: 0
  });

  useEffect(() => {
    if (editingQuote) {
      setFormData({
        number: editingQuote.number || '',
        companyId: editingQuote.companyId || '',
        date: editingQuote.date ? editingQuote.date.split('T')[0] : new Date().toISOString().split('T')[0],
        validUntil: editingQuote.validUntil ? editingQuote.validUntil.split('T')[0] : '',
        status: editingQuote.status || 'DRAFT',
        notes: editingQuote.notes || '',
        object: editingQuote.object || ''
      });
      
      // Mapper correctement les items du backend vers le format du formulaire
      if (editingQuote.items && editingQuote.items.length > 0) {
        const mappedItems = editingQuote.items.map(item => ({
          id: item.id || Date.now() + Math.random(),
          description: item.description || '',
          reference: item.reference || '',
          quantity: item.quantity || 1,
          unit: item.unit || 'unité',
          unitPrice: item.unitPrice || 0,
          discount: item.discount || 0,
          vatRate: item.vatRate !== undefined ? item.vatRate : 20,
          totalHT: item.totalHT || 0,
          totalTTC: item.totalTTC || 0,
          deliveryDate: item.deliveryDate || ''
        }));
        setItems(mappedItems);
        
        // Détecter si toutes les lignes ont TVA à 0%
        const allZeroVat = mappedItems.every(item => item.vatRate === 0);
        setGlobalVatZero(allZeroVat);
      } else {
        setItems([{
          id: Date.now(),
          description: '',
          reference: '',
          quantity: 1,
          unit: 'unité',
          unitPrice: 0,
          discount: 0,
          vatRate: 20,
          totalHT: 0,
          totalTTC: 0,
          deliveryDate: ''
        }]);
      }
    } else {
      // Nouveau devis - générer numéro automatiquement
      const nextNumber = `DEV-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`;
      const profile = loadCompanyProfile();
      const liable = profile?.vatLiable !== undefined ? profile.vatLiable : true;
      const defaultRate = profile?.defaultVatRate !== undefined ? profile.defaultVatRate : 20;
      const activity = loadActivityMode();
      const defaults = getDefaultQuoteItemsForActivity(activity?.id, {
        defaultVatRate: liable ? defaultRate : 0,
        globalVatZero: !liable,
      });

      setFormData({
        number: nextNumber,
        companyId: '',
        date: new Date().toISOString().split('T')[0],
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // +30 jours
        status: 'DRAFT',
        notes: '',
        object: ''
      });

      if (defaults.length) {
        setItems(defaults);
      } else {
        setItems([
          {
            id: Date.now(),
            description: '',
            reference: '',
            quantity: 1,
            unit: 'unité',
            unitPrice: 0,
            discount: 0,
            vatRate: liable ? defaultRate : 0,
            totalHT: 0,
            totalTTC: 0,
            deliveryDate: ''
          }
        ]);
      }
    }
  }, [editingQuote, open]);

  useEffect(() => {
    calculateTotals();
  }, [items, totals.discount]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateItemTotal = (item) => {
    let subtotal = item.quantity * item.unitPrice;
    // Appliquer la remise si elle existe
    if (item.discount && item.discount > 0) {
      subtotal = subtotal * (1 - item.discount / 100);
    }
    const totalHT = subtotal;
    const totalTTC = totalHT * (1 + item.vatRate / 100);
    return { totalHT, totalTTC };
  };

  const calculateTotals = () => {
    let subtotalHT = 0;
    let vatAmount = 0;
    
    items.forEach(item => {
      let itemSubtotal = item.quantity * item.unitPrice;
      // Appliquer la remise de l'item
      if (item.discount && item.discount > 0) {
        itemSubtotal = itemSubtotal * (1 - item.discount / 100);
      }
      const itemVat = itemSubtotal * (item.vatRate / 100);
      subtotalHT += itemSubtotal;
      vatAmount += itemVat;
    });

    const totalTTC = subtotalHT + vatAmount;
    const finalTotal = totalTTC - (totals.discount || 0);

    setTotals({
      subtotalHT,
      vatAmount,
      totalTTC,
      discount: totals.discount || 0,
      finalTotal
    });
  };

  const profileCatalogItems = (catalogItems || []).map(item => ({
    ...item,
    id: item.id || `custom-${item.label}`
  }));

  const combinedCatalogs = [
    ...catalogPresets,
    { id: 'custom', label: 'Mes prestations', items: profileCatalogItems }
  ];

  const optimizedForLabel = (() => {
    switch (selectedSector) {
      case 'electrician':
        return 'Électricien indépendant';
      case 'plumber':
        return 'Plombier indépendant';
      case 'btp':
        return 'Artisan bâtiment';
      case 'freelance':
        return 'Freelance';
      case 'beauty':
        return 'Salon beauté';
      case 'commerce':
        return 'Commerce / vente';
      case 'liberal':
        return 'Profession libérale';
      case 'coach':
        return 'Coach / thérapeute';
      case 'services':
      default:
        return 'votre activité';
    }
  })();

  const currentCatalog = combinedCatalogs.find(section => section.id === selectedSector);
  const customItemsByLabel = new Map(
    profileCatalogItems.map(item => [item.label?.toLowerCase(), item])
  );
  const currentCatalogItems = currentCatalog
    ? currentCatalog.items.map(item => customItemsByLabel.get(item.label?.toLowerCase()) || item)
    : [];
  const filteredCatalogItems = currentCatalogItems.filter(item => {
    if (!catalogSearch) return true;
    const query = catalogSearch.toLowerCase();
    return (
      item.label.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query)
    );
  });

  const toggleCatalogItem = (itemId) => {
    setSelectedCatalogIds(prev => (
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    ));
  };

  const buildItemFromCatalog = (catalogItem) => {
    const baseItem = {
      id: Date.now() + Math.random(),
      description: catalogItem.description || catalogItem.label,
      reference: catalogItem.reference || '',
      quantity: catalogItem.quantity || 1,
      unit: catalogItem.unit || 'unité',
      unitPrice: catalogItem.unitPrice || 0,
      discount: 0,
      vatRate: globalVatZero ? 0 : (catalogItem.vatRate ?? defaultVatRate),
      totalHT: 0,
      totalTTC: 0,
      deliveryDate: ''
    };
    const totals = calculateItemTotal(baseItem);
    return { ...baseItem, ...totals };
  };

  const handleAddSelectedItems = () => {
    if (selectedCatalogIds.length === 0) return;
    const selectedItems = currentCatalogItems.filter(item => selectedCatalogIds.includes(item.id));
    const newItems = selectedItems.map(buildItemFromCatalog);
    setItems(prev => [...prev, ...newItems]);
    setSelectedCatalogIds([]);
  };

  const handleAssistantSuggest = () => {
    const suggested = suggestCatalogSector(assistantInput);
    setSelectedSector(suggested);
  };

  const handleGlobalVatZeroChange = (checked) => {
    setGlobalVatZero(checked);
    const newItems = items.map(item => ({
      ...item,
      vatRate: checked ? 0 : defaultVatRate
    }));
    setItems(newItems);
  };

  const handleOpenCatalogDialog = () => {
    const profile = loadCompanyProfile();
    const existingItems = profile?.catalogItems || [];
    const selectedFromCurrent = currentCatalogItems
      .filter(item => selectedCatalogIds.includes(item.id))
      .map(item => ({
        id: item.id?.startsWith('custom-') ? item.id : `custom-${item.label}-${Date.now()}`,
        label: item.label,
        description: item.description || item.label,
        unit: item.unit || 'unité',
        unitPrice: parseFloat(item.unitPrice) || 0,
        vatRate: parseFloat(item.vatRate) || 20
      }));

    const merged = [...existingItems];
    selectedFromCurrent.forEach(item => {
      const exists = merged.some(existing => existing.label === item.label && existing.unit === item.unit);
      if (!exists) merged.push(item);
    });

    setCatalogItems(merged);
    setSelectedSector('custom');
    setCatalogDialogOpen(true);
  };

  const handleCloseCatalogDialog = () => {
    setCatalogDialogOpen(false);
  };

  const handleCatalogItemChange = (itemId, field, value) => {
    setCatalogItems(prev => prev.map(item => (
      item.id === itemId ? { ...item, [field]: value } : item
    )));
  };

  const handleAddCatalogItem = () => {
    if (!newCatalogItem.label) return;

    const item = {
      id: `custom-${Date.now()}`,
      label: newCatalogItem.label,
      description: newCatalogItem.description || newCatalogItem.label,
      unit: newCatalogItem.unit || 'unité',
      unitPrice: parseFloat(newCatalogItem.unitPrice) || 0,
      vatRate: parseFloat(newCatalogItem.vatRate) || 20
    };

    setCatalogItems(prev => [...prev, item]);
    setNewCatalogItem({
      label: '',
      description: '',
      unit: 'unité',
      unitPrice: '',
      vatRate: 20
    });
  };

  const handleRemoveCatalogItem = (itemId) => {
    setCatalogItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleSaveCatalogItems = () => {
    const profile = loadCompanyProfile() || {};
    const updated = { ...profile, catalogItems };
    saveCompanyProfile(updated);
    window.dispatchEvent(new Event('companyProfileUpdated'));
    setSelectedSector(catalogItems.length ? 'custom' : 'it');
    setCatalogDialogOpen(false);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculer les totaux de l'item
    const { totalHT, totalTTC } = calculateItemTotal(newItems[index]);
    newItems[index].totalHT = totalHT;
    newItems[index].totalTTC = totalTTC;
    
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, {
      id: Date.now(),
      description: '',
      reference: '',
      quantity: 1,
      unit: 'unité',
      unitPrice: 0,
      discount: 0,
      vatRate: globalVatZero ? 0 : defaultVatRate,
      totalHT: 0,
      totalTTC: 0,
      deliveryDate: ''
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = () => {
    // Ne pas envoyer 'number' pour une nouvelle création (généré par l'API)
    const { number, ...dataWithoutNumber } = formData;
    const quoteData = {
      ...(editingQuote ? formData : dataWithoutNumber), // Inclure number seulement en édition
      items: items.map(item => ({
        description: item.description,
        reference: item.reference || '',
        quantity: parseFloat(item.quantity) || 1,
        unit: item.unit || 'unité',
        unitPrice: parseFloat(item.unitPrice) || 0,
        discount: parseFloat(item.discount) || 0,
        vatRate: item.vatRate !== undefined && item.vatRate !== null ? parseFloat(item.vatRate) : 20
      })),
      totalHT: totals.subtotalHT,
      totalTTC: totals.finalTotal,
      vatAmount: totals.vatAmount,
      discount: totals.discount || 0
    };
    // (debug) données envoyées volontairement non loggées en production
    onSave(quoteData);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'default';
      case 'SENT': return 'primary';
      case 'ACCEPTED': return 'success';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {editingQuote ? 'Modifier le devis' : 'Nouveau devis'}
          </Typography>
          <Chip 
            label={formData.status}
            color={getStatusColor(formData.status)}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Informations générales */}
          <Typography variant="h6" gutterBottom color="primary">
            Informations générales
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Numéro de devis"
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  value={formData.companyId}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                  disabled={!companies || companies.length === 0}
                >
                  {(!companies || companies.length === 0) ? (
                    <MenuItem value="" disabled>
                      Aucun client
                    </MenuItem>
                  ) : (
                    companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name} {company.companyType === 'B2C' ? '(Particulier)' : '(Entreprise)'}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            {(!editingQuote && (!companies || companies.length === 0)) && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                  <Typography fontWeight={700}>
                    Vous n'avez pas encore de client.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Créez un client pour commencer.
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        onClose();
                        navigate('/companies');
                      }}
                    >
                      Créer un client
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Valide jusqu'au"
                type="date"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Objet du devis"
                multiline
                rows={2}
                value={formData.object}
                onChange={(e) => setFormData({ ...formData, object: e.target.value })}
                placeholder="Ex: Conception et développement d'un site vitrine"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Prestations */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6" color="primary">
                Prestations
              </Typography>
              <FormControl component="fieldset" size="small">
                <Box display="flex" alignItems="center">
                  <input
                    type="checkbox"
                    id="globalVatZero"
                    checked={globalVatZero}
                    onChange={(e) => handleGlobalVatZeroChange(e.target.checked)}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  />
                  <label htmlFor="globalVatZero" style={{ cursor: 'pointer', fontSize: '14px' }}>
                    TVA à 0% (toutes les lignes)
                  </label>
                </Box>
              </FormControl>
            </Box>
            <Button
              startIcon={<AddIcon />}
              onClick={addItem}
              variant="outlined"
              size="small"
            >
              Ajouter une prestation
            </Button>
          </Box>

          <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px dashed', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Catalogue rapide (cases à cocher)
              </Typography>
              <Button variant="outlined" size="small" onClick={handleOpenCatalogDialog}>
                Modifier le catalogue
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Catalogue optimisé pour : {optimizedForLabel}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Secteur</InputLabel>
                  <Select
                    label="Secteur"
                    value={selectedSector}
                    onChange={(e) => setSelectedSector(e.target.value)}
                  >
                    {combinedCatalogs.map(section => (
                      <MenuItem key={section.id} value={section.id}>
                        {section.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {selectedSector === 'plumber' ? (
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={plumberUrgence}
                          onChange={(e) => setPlumberUrgence(e.target.checked)}
                        />
                      }
                      label="Urgence (majoration auto)"
                    />
                  </Box>
                </Grid>
              ) : null}

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Recherche"
                  value={catalogSearch}
                  onChange={(e) => setCatalogSearch(e.target.value)}
                  placeholder="Ex: maintenance, site web"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Assistant (que facturez-vous ?)"
                    value={assistantInput}
                    onChange={(e) => setAssistantInput(e.target.value)}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAssistantSuggest}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Suggérer
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Conseil : utilisez le secteur « Mes prestations » pour vos cases personnalisées.
            </Typography>
            {filteredCatalogItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Aucun résultat pour ce secteur.
              </Typography>
            ) : (
              <Grid container spacing={1}>
                {filteredCatalogItems.map(item => (
                  <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedCatalogIds.includes(item.id)}
                          onChange={() => toggleCatalogItem(item.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {item.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.unitPrice} € / {item.unit}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleAddSelectedItems}
                disabled={selectedCatalogIds.length === 0}
                sx={(theme) => ({
                  color: theme.palette.primary.contrastText,
                  '&.Mui-disabled': {
                    color: alpha(theme.palette.primary.contrastText, 0.78),
                    backgroundColor: alpha(theme.palette.primary.main, 0.42),
                  },
                })}
              >
                Ajouter la sélection
              </Button>
              <Button
                variant="text"
                onClick={() => setSelectedCatalogIds([])}
              >
                Tout décocher
              </Button>
            </Box>
          </Box>

          <Dialog open={catalogDialogOpen} onClose={handleCloseCatalogDialog} maxWidth="md" fullWidth>
            <DialogTitle>Catalogue rapide - Mes prestations</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Libellé"
                    value={newCatalogItem.label}
                    onChange={(e) => setNewCatalogItem({ ...newCatalogItem, label: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={newCatalogItem.description}
                    onChange={(e) => setNewCatalogItem({ ...newCatalogItem, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Unité</InputLabel>
                    <Select
                      label="Unité"
                      value={newCatalogItem.unit}
                      onChange={(e) => setNewCatalogItem({ ...newCatalogItem, unit: e.target.value })}
                    >
                      <MenuItem value="unité">unité</MenuItem>
                      <MenuItem value="h">heure</MenuItem>
                      <MenuItem value="j">jour</MenuItem>
                      <MenuItem value="mois">mois</MenuItem>
                      <MenuItem value="forfait">forfait</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={1}>
                  <TextField
                    fullWidth
                    label="Prix"
                    type="number"
                    value={newCatalogItem.unitPrice}
                    onChange={(e) => setNewCatalogItem({ ...newCatalogItem, unitPrice: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6} md={1}>
                  <FormControl fullWidth>
                    <InputLabel>TVA</InputLabel>
                    <Select
                      label="TVA"
                      value={newCatalogItem.vatRate}
                      onChange={(e) => setNewCatalogItem({ ...newCatalogItem, vatRate: e.target.value })}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5.5}>5,5%</MenuItem>
                      <MenuItem value={10}>10%</MenuItem>
                      <MenuItem value={20}>20%</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="contained" onClick={handleAddCatalogItem}>
                  Ajouter
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              {catalogItems.length ? (
                <Grid container spacing={1}>
                  {catalogItems.map((item) => (
                    <Grid item xs={12} key={item.id}>
                      <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                          label="Libellé"
                          value={item.label}
                          onChange={(e) => handleCatalogItemChange(item.id, 'label', e.target.value)}
                          sx={{ minWidth: 180, flex: 1 }}
                        />
                        <TextField
                          label="Description"
                          value={item.description}
                          onChange={(e) => handleCatalogItemChange(item.id, 'description', e.target.value)}
                          sx={{ minWidth: 220, flex: 2 }}
                        />
                        <FormControl sx={{ minWidth: 110 }}>
                          <InputLabel>Unité</InputLabel>
                          <Select
                            label="Unité"
                            value={item.unit}
                            onChange={(e) => handleCatalogItemChange(item.id, 'unit', e.target.value)}
                          >
                            <MenuItem value="unité">unité</MenuItem>
                            <MenuItem value="h">heure</MenuItem>
                            <MenuItem value="j">jour</MenuItem>
                            <MenuItem value="mois">mois</MenuItem>
                            <MenuItem value="forfait">forfait</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          label="Prix"
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleCatalogItemChange(item.id, 'unitPrice', e.target.value)}
                          sx={{ width: 110 }}
                        />
                        <FormControl sx={{ width: 90 }}>
                          <InputLabel>TVA</InputLabel>
                          <Select
                            label="TVA"
                            value={item.vatRate}
                            onChange={(e) => handleCatalogItemChange(item.id, 'vatRate', e.target.value)}
                          >
                            <MenuItem value={0}>0%</MenuItem>
                            <MenuItem value={5.5}>5,5%</MenuItem>
                            <MenuItem value={10}>10%</MenuItem>
                            <MenuItem value={20}>20%</MenuItem>
                          </Select>
                        </FormControl>
                        <IconButton color="error" onClick={() => handleRemoveCatalogItem(item.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucun élément personnalisé pour le moment.
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCatalogDialog}>Annuler</Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveCatalogItems}>
                Enregistrer
              </Button>
            </DialogActions>
          </Dialog>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ 
                  backgroundColor: '#1F2937',
                  '& .MuiTableCell-root': {
                    color: '#FFFFFF',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #374151'
                  }
                }}>
                  {activeColumns.includes('reference') && <TableCell>Référence</TableCell>}
                  {activeColumns.includes('description') && <TableCell>Description</TableCell>}
                  {activeColumns.includes('quantity') && <TableCell align="center">Qté</TableCell>}
                  {activeColumns.includes('unit') && <TableCell align="center">Unité</TableCell>}
                  {activeColumns.includes('unitPrice') && <TableCell align="right">Prix unitaire HT</TableCell>}
                  {activeColumns.includes('discount') && <TableCell align="center">Remise %</TableCell>}
                  {activeColumns.includes('vatRate') && <TableCell align="center">TVA %</TableCell>}
                  {activeColumns.includes('totalHT') && <TableCell align="right">Total HT</TableCell>}
                  {activeColumns.includes('totalTTC') && <TableCell align="right">Total TTC</TableCell>}
                  {activeColumns.includes('deliveryDate') && <TableCell align="center">Livraison</TableCell>}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={item.id}>
                    {activeColumns.includes('reference') && (
                      <TableCell>
                        <TextField
                          value={item.reference}
                          onChange={(e) => handleItemChange(index, 'reference', e.target.value)}
                          placeholder="REF-001"
                          size="small"
                          sx={{ 
                            width: 120,
                            '& .MuiInputBase-input::placeholder': {
                              color: 'text.secondary',
                              opacity: 0.7
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    {activeColumns.includes('description') && (
                      <TableCell>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Décrivez la prestation"
                          variant="outlined"
                          size="small"
                          sx={{
                            '& .MuiInputBase-input::placeholder': {
                              color: 'text.secondary',
                              opacity: 0.7
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    {activeColumns.includes('quantity') && (
                      <TableCell>
                        <TextField
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.5 }}
                          size="small"
                          placeholder="0"
                          sx={{ 
                            width: 80,
                            '& .MuiInputBase-input::placeholder': {
                              color: 'text.secondary',
                              opacity: 0.6
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    {activeColumns.includes('unit') && (
                      <TableCell>
                        <FormControl size="small" sx={{ width: 80 }}>
                          <Select
                            value={item.unit}
                            onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          >
                            <MenuItem value="unité">unité</MenuItem>
                            <MenuItem value="h">heure</MenuItem>
                            <MenuItem value="j">jour</MenuItem>
                            <MenuItem value="mois">mois</MenuItem>
                            <MenuItem value="forfait">forfait</MenuItem>
                            <MenuItem value="kg">kg</MenuItem>
                            <MenuItem value="m">mètre</MenuItem>
                            <MenuItem value="m²">m²</MenuItem>
                            <MenuItem value="L">litre</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                    )}
                    {activeColumns.includes('unitPrice') && (
                      <TableCell>
                        <TextField
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                          size="small"
                          placeholder="0.00"
                          sx={{ 
                            width: 120,
                            '& .MuiInputBase-input::placeholder': {
                              color: 'text.secondary',
                              opacity: 0.6
                            }
                          }}
                          InputProps={{ endAdornment: '€' }}
                        />
                      </TableCell>
                    )}
                    {activeColumns.includes('discount') && (
                      <TableCell>
                        <TextField
                          type="number"
                          value={item.discount}
                          onChange={(e) => handleItemChange(index, 'discount', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, max: 100, step: 1 }}
                          size="small"
                          placeholder="0"
                          sx={{ 
                            width: 80,
                            '& .MuiInputBase-input::placeholder': {
                              color: 'text.secondary',
                              opacity: 0.6
                            }
                          }}
                          InputProps={{ endAdornment: '%' }}
                        />
                      </TableCell>
                    )}
                    {activeColumns.includes('vatRate') && (
                      <TableCell>
                        <FormControl size="small" sx={{ width: 80 }}>
                          <Select
                            value={item.vatRate}
                            onChange={(e) => handleItemChange(index, 'vatRate', parseFloat(e.target.value))}
                          >
                            <MenuItem value={0}>0%</MenuItem>
                            <MenuItem value={5.5}>5,5%</MenuItem>
                            <MenuItem value={10}>10%</MenuItem>
                            <MenuItem value={20}>20%</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                    )}
                    {activeColumns.includes('totalHT') && (
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.totalHT.toFixed(2)} €
                        </Typography>
                      </TableCell>
                    )}
                    {activeColumns.includes('totalTTC') && (
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.totalTTC.toFixed(2)} €
                        </Typography>
                      </TableCell>
                    )}
                    {activeColumns.includes('deliveryDate') && (
                      <TableCell>
                        <TextField
                          type="date"
                          value={item.deliveryDate}
                          onChange={(e) => handleItemChange(index, 'deliveryDate', e.target.value)}
                          size="small"
                          sx={{ 
                            width: 140,
                            '& .MuiInputBase-input::placeholder': {
                              color: 'text.secondary',
                              opacity: 0.6
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <IconButton
                        onClick={() => removeItem(index)}
                        disabled={items.length === 1}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 3 }} />

          {/* Totaux */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes supplémentaires..."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                  <CalculateIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Récapitulatif
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Sous-total HT:</Typography>
                  <Typography>{totals.subtotalHT.toFixed(2)} €</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>TVA:</Typography>
                  <Typography>{totals.vatAmount.toFixed(2)} €</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Total TTC:</Typography>
                  <Typography>{totals.totalTTC.toFixed(2)} €</Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Remise"
                  type="number"
                  value={totals.discount}
                  onChange={(e) => setTotals({ ...totals, discount: parseFloat(e.target.value) || 0 })}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{ endAdornment: '€' }}
                  size="small"
                  sx={{ my: 1 }}
                />
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">TOTAL FINAL:</Typography>
                  <Typography variant="h6" color="primary">
                    {totals.finalTotal.toFixed(2)} €
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Annuler
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          color="primary"
          disabled={!formData.number || !formData.companyId || items.length === 0 || !companies || companies.length === 0}
          sx={{
            color: '#FFFFFF !important',
            fontWeight: 'bold',
            '&:hover': {
              opacity: 0.9
            }
          }}
        >
          {editingQuote ? 'Modifier' : 'Créer'} le devis
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedQuoteDialog;